/**
 * useAdmin Hook
 * 
 * Provides admin status check for the current user.
 * Admin user IDs are configured via VITE_ADMIN_USER_IDS environment variable.
 */

import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';

// Admin user IDs from environment variable
const ADMIN_USER_IDS = (import.meta.env.VITE_ADMIN_USER_IDS || '').split(',').filter(Boolean);

/**
 * Check if the current user is an admin
 * @returns Object with isAdmin boolean and loading state
 */
export function useAdmin() {
  const { user, loading } = useAuth();
  
  const isAdmin = useMemo(() => {
    if (!user?.id) return false;
    return ADMIN_USER_IDS.includes(user.id);
  }, [user?.id]);
  
  return {
    isAdmin,
    isLoading: loading,
    adminUserIds: ADMIN_USER_IDS,
  };
}

/**
 * Check if a specific user ID is an admin
 * @param userId - The user ID to check
 * @returns boolean
 */
export function isUserAdmin(userId: string | undefined): boolean {
  if (!userId) return false;
  return ADMIN_USER_IDS.includes(userId);
}

export default useAdmin;
